import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import {
  GENERATE_MY_COVER_IMAGE,
  MY_COVER_GENERATION,
  MY_COVER_GENERATION_QUOTA,
  type AiCoverQuota,
  type CoverAspect,
  type GenerateMyCoverImageData,
  type GenerateMyCoverImageVars,
  type MyCoverGenerationData,
  type MyCoverGenerationQuotaData,
  type MyCoverGenerationVars,
} from '@/queries/aiCover';
import { mapAiCoverError, type AiCoverErrorCode } from '@/lib/cover/mapAiCoverError';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 4 * 60 * 1000;

export type AiCoverGenerationStatus = 'idle' | 'generating' | 'done' | 'error';

export interface AiCoverGenerationState {
  status: AiCoverGenerationStatus;
  resultUrl?: string;
  error?: AiCoverErrorCode;
}

export interface GenerateAiCoverInput {
  clipId: string;
  prompt?: string | null;
  referenceUrl?: string | null;
  aspect: CoverAspect;
}

export interface UseAiCoverGenerationResult {
  state: AiCoverGenerationState;
  quota: AiCoverQuota | null;
  refetchQuota: () => Promise<void>;
  generate: (input: GenerateAiCoverInput) => Promise<void>;
  reset: () => void;
}

/**
 * Drives `generateMyCoverImage` and polls `myCoverGeneration` every
 * `POLL_INTERVAL_MS` until it reaches a terminal `processing_status`
 * (`done`/`failed`) or `POLL_TIMEOUT_MS` elapses. Because the client-wide
 * Apollo default is `fetchPolicy: 'no-cache'` (see `src/lib/apollo-client.ts`),
 * polling reads through `client.query({ fetchPolicy: 'network-only' })`
 * rather than a cache-driven `useQuery`.
 *
 * A poll that fails without a mapped error code (network blip, 5xx) is
 * retried on the next tick; only a mapped code (e.g. `not_found`) or the
 * deadline ends the flow.
 *
 * `generationRef` is a monotonically increasing counter: starting a new
 * `generate()` call bumps it, and every in-flight timer/promise checks it
 * before touching state, so a superseded generation's late poll response is
 * silently dropped instead of clobbering the newer one.
 */
export function useAiCoverGeneration(): UseAiCoverGenerationResult {
  const client = useApolloClient();
  const [generateMutation] = useMutation<GenerateMyCoverImageData, GenerateMyCoverImageVars>(
    GENERATE_MY_COVER_IMAGE,
  );

  const [state, setState] = useState<AiCoverGenerationState>({ status: 'idle' });
  const [quota, setQuota] = useState<AiCoverQuota | null>(null);

  const generationRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (deadlineTimerRef.current) {
      clearTimeout(deadlineTimerRef.current);
      deadlineTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    // Bump the generation so any in-flight poll promise that resolves after
    // unmount is recognized as stale and skips its setState.
    generationRef.current += 1;
    clearTimers();
  }, [clearTimers]);

  const refetchQuota = useCallback(async () => {
    try {
      const { data } = await client.query<MyCoverGenerationQuotaData>({
        query: MY_COVER_GENERATION_QUOTA,
        fetchPolicy: 'network-only',
      });
      if (data?.myCoverGenerationQuota) setQuota(data.myCoverGenerationQuota);
    } catch {
      // Best-effort: a stale quota value is acceptable, the dialog will
      // refresh it again on the next generation.
    }
  }, [client]);

  const reset = useCallback(() => {
    generationRef.current += 1;
    clearTimers();
    setState({ status: 'idle' });
  }, [clearTimers]);

  const schedulePoll = useCallback((id: string, generation: number) => {
    pollTimerRef.current = setTimeout(() => {
      pollTimerRef.current = null;
      if (generation !== generationRef.current) return;

      client
        .query<MyCoverGenerationData, MyCoverGenerationVars>({
          query: MY_COVER_GENERATION,
          variables: { id },
          fetchPolicy: 'network-only',
        })
        .then((response) => {
          if (generation !== generationRef.current) return;

          const generationRow = response.data?.myCoverGeneration;
          if (!generationRow) {
            schedulePoll(id, generation);
            return;
          }

          if (generationRow.processing_status === 'done') {
            clearTimers();
            // Guard: the backend never marks a row done without a URL, but a
            // "done" with nothing to show must not look like success.
            if (generationRow.result_url) {
              setState({ status: 'done', resultUrl: generationRow.result_url });
            } else {
              setState({ status: 'error', error: 'generation_failed' });
            }
            void refetchQuota();
            return;
          }

          if (generationRow.processing_status === 'failed') {
            clearTimers();
            setState({ status: 'error', error: 'generation_failed' });
            void refetchQuota();
            return;
          }

          schedulePoll(id, generation);
        })
        .catch((err: unknown) => {
          if (generation !== generationRef.current) return;
          const code = mapAiCoverError(err);
          if (code === 'unknown') {
            // Transient (network/5xx): the job keeps running server-side, so
            // keep polling until a terminal state or the deadline timer.
            schedulePoll(id, generation);
            return;
          }
          clearTimers();
          setState({ status: 'error', error: code });
        });
    }, POLL_INTERVAL_MS);
  }, [client, clearTimers, refetchQuota]);

  const generate = useCallback(async (input: GenerateAiCoverInput) => {
    clearTimers();
    generationRef.current += 1;
    const generation = generationRef.current;
    setState({ status: 'generating' });

    try {
      const { data } = await generateMutation({
        variables: {
          clipId: input.clipId,
          prompt: input.prompt ?? null,
          referenceUrl: input.referenceUrl ?? null,
          aspect: input.aspect,
        },
      });

      if (generation !== generationRef.current) return;

      const created = data?.generateMyCoverImage;
      if (!created) {
        setState({ status: 'error', error: 'unknown' });
        return;
      }

      deadlineTimerRef.current = setTimeout(() => {
        if (generation !== generationRef.current) return;
        clearTimers();
        setState({ status: 'error', error: 'timeout' });
      }, POLL_TIMEOUT_MS);

      schedulePoll(created.id, generation);
    } catch (err: unknown) {
      if (generation !== generationRef.current) return;
      setState({ status: 'error', error: mapAiCoverError(err) });
    }
  }, [clearTimers, generateMutation, schedulePoll]);

  return { state, quota, refetchQuota, generate, reset };
}
